import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import logo from "@/assets/logo.png";
import { ShieldCheck } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("password"); // password | mfa
  const [form, setForm] = useState({ email: "", password: "" });
  const [code, setCode] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await login(form.email, form.password);
      if (res.mfaRequired) { setChallengeToken(res.challengeToken); setStep("mfa"); }
      else navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await verifyMfa(challengeToken, code);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm fade-up">
        <img src={logo} alt="AI Assurances" className="h-9 w-auto mx-auto mb-8" data-testid="login-brand-logo" />
          {step === "password" ? (
            <>
              <h2 className="font-head font-bold text-2xl mb-1">Connexion</h2>
              <p className="text-sm text-muted-foreground mb-6">Accédez à votre espace.</p>
              <form onSubmit={submitPassword} className="space-y-4">
                <div>
                  <label className="overline text-muted-foreground">Email</label>
                  <input data-testid="email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required className="w-full mt-1 px-3 py-2.5 border border-border focus:border-primary outline-none text-sm" />
                </div>
                <div>
                  <label className="overline text-muted-foreground">Mot de passe</label>
                  <input data-testid="password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required className="w-full mt-1 px-3 py-2.5 border border-border focus:border-primary outline-none text-sm" />
                </div>
                {error && <div data-testid="auth-error" className="text-sm text-destructive bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
                <button data-testid="submit-auth-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-primary text-white font-semibold text-sm hover:bg-blue-800 transition-colors disabled:opacity-60">
                  {loading ? "…" : "Se connecter"}
                </button>
              </form>
              <p className="mt-4 text-xs text-muted-foreground">L'accès est réservé aux membres invités. Contactez votre administrateur pour obtenir un compte.</p>
            </>
          ) : (
            <>
              <h2 className="font-head font-bold text-2xl mb-1 flex items-center gap-2"><ShieldCheck size={22} className="text-primary" /> Vérification</h2>
              <p className="text-sm text-muted-foreground mb-6">Saisissez le code à 6 chiffres de votre application d'authentification (ou un code de récupération).</p>
              <form onSubmit={submitMfa} className="space-y-4">
                <div>
                  <label className="overline text-muted-foreground">Code de vérification</label>
                  <input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} autoFocus
                    placeholder="123 456" inputMode="numeric"
                    className="w-full mt-1 px-3 py-2.5 border border-border focus:border-primary outline-none text-sm font-mono tracking-widest" />
                </div>
                {error && <div data-testid="mfa-error" className="text-sm text-destructive bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
                <button data-testid="mfa-verify-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-primary text-white font-semibold text-sm hover:bg-blue-800 transition-colors disabled:opacity-60">
                  {loading ? "…" : "Vérifier"}
                </button>
                <button type="button" onClick={() => { setStep("password"); setCode(""); setError(""); }}
                  className="w-full text-sm text-muted-foreground hover:text-primary">Retour</button>
              </form>
            </>
          )}
        </div>
      </div>
  );
}
